import React, { useState, useEffect, useContext, useCallback, useRef } from "react";
import config from "config";
import { upperCaseFirstLetter } from "Helpers/utils";
import { MsalContext, useMsal } from "Auth/msal-context";
import { messengerRequest } from "Auth/auth-config";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { closeSocket } from "./ducks/socketDuck";
import { setNetworkConnection, getConfig } from "./ducks/systemDuck";
import { fetchDevicesConfigs } from "./ducks/devicesConfigsDuck";
import { setCommandButtonDisabled } from "./ducks/uiDuck";
import {
  useSocketConnectionDevicesMessageTypeEvent,
  useSocketConnectionStateChangedUpdateEvent,
  useSocketConnectionAlarmsMessageTypeEvent,
} from "./helpers/useSocketConnection";
import DashboardTopBar from "./components/dashboardTopBar/DashboardTopBar";
import DashboardContent from "./components/DashboardContent";
import AlarmsSideBarContainer from "./containers/AlarmsSideBarContainer";
import { KeyboardProvider } from "./hooks/KeyboardContext";
import { Numpad } from "./components/common";
import SystemIdContext from "./SystemIdContext";
import { surface } from "../theme/muiTheme";
import {
  selectSystemConfig,
  selectIsNetworkConnected,
  selectLanguage,
  selectSolutionType,
  selectSocketConnected,
  selectSelectedSystems,
} from "./ducks/selectors";
import { SnackbarProvider } from "notistack";
import NotificationListener from "./components/common/snackbar/NotificationListener";
import { MaterialDesignContent } from "notistack";
import { styled, Box, CircularProgress, useMediaQuery, useTheme } from "@mui/material";
import { selectUI } from "./ducks/selectors";

export { default as SystemIdContext } from "./SystemIdContext";

const StyledMaterialDesignContent = styled(MaterialDesignContent)(() => ({
  "&.notistack-MuiContent-info": {
    backgroundColor: surface.panel,
  },
}));

const Dashboard = ({ systemId, systemName, dashboardCount }) => {
  const [currentMenuLabel, setCurrentMenuLabel] = useState(
    upperCaseFirstLetter("overview")
  );

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isCompact = isMobile || (dashboardCount || 1) > 1;
  const { isEdge } = useMsal();
  const contentRef = useRef(null);
  const [leftDrawerOpen, setLeftDrawerOpen] = useState(false);
  const [rightDrawerOpen, setRightDrawerOpen] = useState(false);
  const ui = useSelector((state) => selectUI(state, systemId));

  const msalContext = useContext(MsalContext);
  const { i18n } = useTranslation("common");
  const dispatch = useDispatch();
  // Dashboard creates the SystemIdContext.Provider, so its own hooks can't use context.
  // Use inline selectors with the systemId prop instead.
  const language = useSelector((state) => selectLanguage(state, systemId)) ?? "en";
  const systemConfig = useSelector((state) => selectSystemConfig(state, systemId));
  const isNetworkConnected = useSelector((state) => selectIsNetworkConnected(state, systemId));
  const selectedSystems = useSelector(selectSelectedSystems);
  const solutionType = useSelector((state) => selectSolutionType(state, systemId));
  const socketConnected = useSelector((state) => selectSocketConnected(state, systemId));
  const isInitialized = useSelector(({ config }) => config[systemId]?._initialized);
  const dashboardItem = selectedSystems?.find((sys) => sys.deviceId === systemId);
  const serviceModeActive = ui?.serviceMode?.isActive;
  console.log("serviceModeActive", serviceModeActive);

  // Lazy-initialize cloud device state when dashboard opens for the first time
  useEffect(() => {
    if (isInitialized === false) {
      const payload = { systemId, solutionType };
      dispatch({ type: "config/initializeDeviceConfig", payload });
      dispatch({ type: "realtime/initializeDeviceRealtime", payload });
      dispatch({ type: "historical/initializeDeviceHistorical", payload });
      dispatch({ type: "ui/initializeDeviceUI", payload });
    }
  }, [systemId, isInitialized, solutionType, dispatch]);

  // Socket event listeners (moved from Routes.jsx)
  useSocketConnectionDevicesMessageTypeEvent(socketConnected, systemId, dispatch);
  useSocketConnectionStateChangedUpdateEvent(socketConnected, systemId, dispatch);
  useSocketConnectionAlarmsMessageTypeEvent(socketConnected, systemId, dispatch);

  useEffect(() => {
    // Wait for lazy initialization in cloud before dispatching setup actions
    if (isInitialized === false) return;

    msalContext.msalExec(messengerRequest, (accessToken) => {
      if (systemId !== "edge" && config) {
        dispatch(
          getConfig({
            systemId,
            data: {
              ...config,
              CUSTOMER_ID: dashboardItem.customerId,
              DEVICE_ID: dashboardItem.deviceId,
              CONFIG_FILE: dashboardItem.config,
              accessToken,
              user: { homeAccountId: msalContext.user.homeAccountId },
            },
          })
        );
      } else {
        dispatch(getConfig({ systemId, data: { ...config } }));
      }
    });

    i18n.changeLanguage(language || "en");
    const pathName = window.location.pathname.slice(1);
    setCurrentMenuLabel(upperCaseFirstLetter(pathName || "overview"));
    dispatch(setCommandButtonDisabled({ data: { isServiceModeBtnDisabled: false, commandType: "serviceMode" }, systemId }));
    dispatch(setCommandButtonDisabled({ data: { isAutoOrManualBtnDisabled: false, commandType: "AutoOrManual" }, systemId }));

    const handleConnectionChange = () => {
      dispatch(setNetworkConnection({ isNetworkConnected: navigator.onLine, systemId }));
    };

    handleConnectionChange();
    window.addEventListener("online", handleConnectionChange);
    window.addEventListener("offline", handleConnectionChange);

    return () => {
      window.removeEventListener("online", handleConnectionChange);
      window.removeEventListener("offline", handleConnectionChange);
      dispatch(closeSocket({ systemId }));
    };
  }, [systemId, dispatch, isInitialized]);

  useEffect(() => {
    if (systemConfig && Object.keys(systemConfig).length > 0 && isNetworkConnected) {
      dispatch(
        fetchDevicesConfigs({
          systemId,
          systemConfig,
          isSensor: solutionType === "lora",
        })
      );
    }
  }, [systemConfig, isNetworkConnected]);

  const onSelect = useCallback((selectedMenu) => {
    setCurrentMenuLabel(selectedMenu ? upperCaseFirstLetter(selectedMenu) : selectedMenu);
  }, []);

  // Don't render children until cloud state is fully initialized
  if (isInitialized === false) {
    return (
      <SystemIdContext.Provider value={systemId}>
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
          <CircularProgress />
        </Box>
      </SystemIdContext.Provider>
    );
  }

  return (
    <SystemIdContext.Provider value={systemId}>
      <SnackbarProvider
        maxSnack={4}
        autoHideDuration={5000}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        style={{ zIndex: 9999 }}
        Components={{ info: StyledMaterialDesignContent }}
      >
        <NotificationListener systemName={systemName} />
        <KeyboardProvider>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              height: "100%",
              border: "1px solid",
              borderColor: theme.palette.dashboard.border,
              borderRadius: 1,
              overflow: "hidden",
              background: theme.palette.dashboard.backgroundGradient,
            }}
          >
            <DashboardTopBar
              currentMenuLabel={currentMenuLabel}
              dashboardCount={dashboardCount || 1}
              isCompact={isCompact}
              onToggleLeftDrawer={() => setLeftDrawerOpen(prev => !prev)}
              onToggleRightDrawer={() => setRightDrawerOpen(prev => !prev)}
            />
            <Box
              ref={contentRef}
              sx={{
                display: "flex",
                flex: "1 1 0",
                minHeight: 0,
                maxHeight: config.APP_ENV === "Edge" ? "100vh" : undefined,
                // "clip" (not "hidden") so the drawer's off-screen slide transition can't make this
                // a programmatically-scrollable container — otherwise the Modal's focus scrollIntoView
                // scrolls the overview content sideways when the alarms bell drawer opens.
                overflow: "clip",
                position: "relative"
              }}>
              <DashboardContent
                currentSelectedMenu={currentMenuLabel}
                onSelect={onSelect}
                systemName={systemName}
                isCompact={isCompact}
                isMobile={isMobile}
                containerRef={contentRef}
                leftDrawerOpen={leftDrawerOpen}
                onCloseLeftDrawer={() => setLeftDrawerOpen(false)}
                dashboardCount={dashboardCount || 1}
              />
              <AlarmsSideBarContainer
                isCompact={isCompact}
                isMobile={isMobile}
                containerRef={contentRef}
                rightDrawerOpen={rightDrawerOpen}
                onCloseRightDrawer={() => setRightDrawerOpen(false)}
              />
            </Box>
            {/* Docked once here, below the whole dashboard content, so the on-screen
                keyboard is always visible and pushes every view up regardless of that
                view's own internal scrolling. */}
            {isEdge && <Numpad />}
          </Box>
        </KeyboardProvider>
      </SnackbarProvider>
    </SystemIdContext.Provider>
  );
};

export default Dashboard;
